import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  Address,
  BuildingWithConvertedAddresses,
  Comment,
  LatLng,
  OffMarketToastMessage,
  OffMarketWillingnessEvent,
} from '../../utils/types';
import type { AddMyPlaceSeed } from '../../AppContent/NewLandingPage/AddMyPlaceFlow/AddMyPlaceFlow';
import type { ListingBuildingGeometry } from '../../AppContent/NewLandingPage/AddMyPlaceFlow/resolveListingBuilding';
import { getCookie } from '../../utils/utils';
import {
  fetchConversationIdMap,
  mergeConversationIds,
  useConversationBadgeRefreshOnLogin,
} from '../../utils/conversationBadges';
import { onlyLatest } from '../../utils/onlyLatest';
import {
  getBuildingLikeCoordinates,
  withLikeRequestCoordinates,
} from '../../utils/buildingLike';
import { getBuildingListings } from '../../utils/buildingListings';
import { useBuildingSubscription } from './useBuildingSubscription';
import {
  getAreaInterestsForBuilding,
  AreaDemandCard,
} from '../../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import DemandBuyerCard from '../../AppContent/NewLandingPage/DemandBuyerCard';
import { shareUrlWithFallback } from '../../../utils/shareUtils';
import {
  formatStreetLine,
  formatAddressLine,
  hasStreetAddress,
} from '../../utils/addressFormatting';
import { listingShareUrl, buildingShareUrl } from '../../../utils/shareUrls';
import { useOpenListing } from '../../utils/useOpenListing';
import LoadingSpinner from '../../common/LoadingSpinner';
import BuildingInfoSkeleton from './BuildingInfoSkeleton';
import ListingCard from '../../ListingCard';
import { ListingCardListing } from '../../ListingCard/types';
import ListingCardManageMenu from '../../ListingCard/ListingCardManageMenu';
import { useListingManagement } from '../../ListingCard/useListingManagement';
import HorizontalCarousel from '../../HorizontalCarousel';
import ListingCardRow from '../../ListingCard/ListingCardRow';
import StreetView from '../StreetView/StreetView';
import BuildingLocationMap from '../BuildingLocationMap';
import BuildingHeroMap from './BuildingHeroMap';
import { useSideAddresses } from './useSideAddresses';
import FrontageRun from '../../common/BuildingFrontages/FrontageRun';
import {
  addressKey,
  isLabelAddress,
  resolvePrimaryAddress,
} from '../../HouseMap/sideAddresses';
import useIsDesktop from './useIsDesktop';
import ImageModal from '../ImageModal/ImageModal';
import PageMetaTags from '../PageMetaTags';
import OffMarketToast from '../../common/OffMarketToast/OffMarketToast';
import AdminPrefilledListings from '../../Admin/AdminPrefilledListings';
import { fetchJaakkoComment } from '../Report.fetchExistingReport';
import { useOffMarketIntent } from '../useOffMarketIntent';
import OffMarketIntentModals from '../OffMarketIntentModals';
import backIcon from '../../common/icons/back.svg';
import shareIcon from '../../common/icons/share.svg';
import heartOutlineIcon from '../../common/icons/heart-outline.svg';
import heartFilledIcon from '../../common/icons/heart-filled.png';
import addImageIcon from '../../common/icons/add-image.svg';
import bellIcon from '../../common/icons/bell.svg';
import homeIcon from '../../common/icons/house.png';
import coinIcon from '../../common/icons/coin.png';
import overflowIcon from '../../common/icons/overflow.svg';
import viewMapIcon from '../../common/icons/view-map.svg';
import ctaHouseGraphic from '../../common/icons/house.png';
import ctaCoinGraphic from '../../common/icons/coin.png';
import './BuildingView.scss';

type Props = {
  isLandingPage?: boolean;
  selectedBuilding: BuildingWithConvertedAddresses;
  isLoggedIn: boolean;
  userEmail?: string;
  showImageModal: string | null;
  countryOfFeedContent: string;
  messagesModalVisible: boolean;
  sharedEventId?: string | null;
  showBuildingModalTriggeredFromCouldBuyOrCouldSellModal?: OffMarketToastMessage;
  toggleShowBuildingModal: () => void;
  setSelectedBuilding: (
    building: BuildingWithConvertedAddresses | null,
  ) => void;
  setLoginModalVisible: (visible: boolean) => void;
  refreshFeed: () => void;
  setShowImageModal: (imageUrl: string | null) => void;
  setCountryOfFeedContent: (country: string) => void;
  fetchConversations: () => void;
  setSharedEventId?: (eventId: string | null) => void;
  setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal?: (
    message: OffMarketToastMessage | null,
  ) => void;
  onBuildingPreviewMetaChange?: (
    buildingId: string,
    patch: { likes?: number; followers?: number; liked?: boolean },
  ) => void;
  onBuildingLikeChange?: (
    building: BuildingWithConvertedAddresses,
    liked: boolean,
  ) => void;
  // Launches the AMP "Sell here" flow seeded with this building. When omitted,
  // the sell CTAs fall back to the legacy details-modal intent flow.
  onSellHere?: (seed: AddMyPlaceSeed | null) => void;
  // "View map" / mobile back: a contextual return to exploration, so the map
  // this closes onto re-centres on the building and re-selects it. Distinct from
  // the Jaakko logo, which is the global home action and drops that context.
  onReturnToMap?: (buildingId: string, coords?: LatLng) => void;
  isAdmin?: boolean;
};

const INSIGHTS_PREVIEW_CHARS = 220;

// Intent variants that never float in the docked CTA: the active-status pills
// ("You are selling here" / "You have buying interest here") stay inline in the
// header only, since they report a state rather than offering an action.
const NON_DOCKABLE_VARIANTS = new Set([
  'sellingActive',
  'buyingActive',
]);

// Add-building-photo upload has no backend yet — hide the affordance until it
// does (kept behind a flag, not deleted, so wiring it back is a one-line change).
const SHOW_ADD_IMAGE = false;

/** One building's page, drawn as a layer on the shell's modal stack. */
const BuildingView = (props: Props) => {
  const {
    isLandingPage,
    selectedBuilding,
    isLoggedIn,
    userEmail,
    showImageModal,
    countryOfFeedContent,
    setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal,
    showBuildingModalTriggeredFromCouldBuyOrCouldSellModal,
    toggleShowBuildingModal,
    setSelectedBuilding,
    setLoginModalVisible,
    refreshFeed,
    setShowImageModal,
    setCountryOfFeedContent,
    fetchConversations,
    onBuildingPreviewMetaChange,
    onBuildingLikeChange,
    onSellHere,
    onReturnToMap,
    isAdmin,
  } = props;

  const { t, i18n } = useTranslation();
  const isDesktop = useIsDesktop();
  // Tapping a carousel card opens the shareable listing route (JAA-306).
  const openListing = useOpenListing();
  const history = useHistory();

  const [isLoadingBuilding, setIsLoadingBuilding] = useState<boolean>(true);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [followerCount, setFollowerCount] = useState<number>(0);
  const [userHasLiked, setUserHasLiked] = useState<boolean>(false);
  const [isSubmittingLike, setIsSubmittingLike] = useState<boolean>(false);
  const [emptyReport, setEmptyReport] = useState<any>(null);
  const [isLoadingEmptyReport, setIsLoadingEmptyReport] =
    useState<boolean>(false);
  const [resolvedInsightsBuildingId, setResolvedInsightsBuildingId] = useState<
    string | null
  >(null);
  const insightsRequestIdRef = useRef(0);
  const [isInsightsExpanded, setIsInsightsExpanded] = useState<boolean>(false);

  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState<boolean>(false);

  // JAA-355: after an in-place login, refresh only the conversation badges of
  // the listing rows already loaded — merged by id, so the rest of the view
  // (insights, scroll position, loading states) is untouched.
  const commentsForBadgesRef = useRef(comments);
  useEffect(() => {
    commentsForBadgesRef.current = comments;
  }, [comments]);
  const refreshConversationBadges = useCallback(async () => {
    const listingIds = commentsForBadgesRef.current
      .filter((row) => row.could_sell || row.could_buy)
      .map((row) => row._id);
    try {
      const map = await fetchConversationIdMap(listingIds);
      if (Object.keys(map).length) {
        setComments((prev) => mergeConversationIds(prev, map));
      }
    } catch (error) {
      console.error('Conversation badge refresh failed:', error);
    }
  }, []);
  useConversationBadgeRefreshOnLogin(isLoggedIn, refreshConversationBadges);

  // Anonymized area interests whose footprint covers this building — the wider
  // buyer demand rendered after the building's own buyer cards. The ref-held
  // guard makes only the latest fetch's response apply (building switches and
  // overlapping refreshes race otherwise, since the view is reused).
  const [areaDemand, setAreaDemand] = useState<AreaDemandCard[]>([]);
  const trackLatestAreaDemand = useRef(onlyLatest<AreaDemandCard[]>()).current;

  const [couldSellSelected, setCouldSellSelected] = useState<boolean>(false);
  const [couldBuySelected, setCouldBuySelected] = useState<boolean>(false);

  const [showStickyHeader, setShowStickyHeader] = useState<boolean>(false);
  const [stickyMenuOpen, setStickyMenuOpen] = useState<boolean>(false);
  const [ctaDocked, setCtaDocked] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLHeadingElement>(null);
  const intentRef = useRef<HTMLDivElement>(null);
  const headerTopRef = useRef<HTMLDivElement>(null);
  const stickyMoreRef = useRef<HTMLButtonElement>(null);
  const stickyMenuFirstItemRef = useRef<HTMLButtonElement>(null);

  const [heroFootprint, setHeroFootprint] = useState<
    GeoJSON.Polygon | GeoJSON.MultiPolygon | null
  >(null);

  // The seed/click building reaching the view often lacks the backend's complete
  // mapbox-union-v2 footprint, forcing the hero down its lossy re-query-own-tiles
  // path that clips large buildings. fetchBuilding loads the real geometry from
  // /building; feed it to the hero so its trusted-source path draws it whole.
  // id-tagged so a previous building's geometry never bleeds onto the next.
  const [fetchedGeometry, setFetchedGeometry] = useState<{
    id: string;
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    geometry_source: string;
  } | null>(null);

  const heroBuilding = useMemo(
    () =>
      fetchedGeometry?.id === selectedBuilding.id
        ? ({
            ...selectedBuilding,
            geometry: fetchedGeometry.geometry,
            geometry_source: fetchedGeometry.geometry_source,
          } as BuildingWithConvertedAddresses)
        : selectedBuilding,
    [selectedBuilding, fetchedGeometry],
  );

  const { addresses: sideAddresses, loading: sideAddressesLoading } =
    useSideAddresses(selectedBuilding, heroFootprint);

  /**
   * Resolved primary address: a listing's canonical address when present,
   * otherwise the clicked address corrected to the nearest verified frontage of
   * this building (provisional until the footprint probe resolves).
   */
  const addressObject = useMemo(() => {
    const listing = comments.find(
      (c) => c.feed_event_type === 'OFF_MARKET_WILLINGNESS',
    ) as unknown as OffMarketWillingnessEvent | undefined;
    if (listing?.address?.street) return listing.address;
    const clicked =
      selectedBuilding.addresses?.[0] ??
      selectedBuilding.properties?.addresses?.[0] ??
      null;
    return resolvePrimaryAddress(
      clicked,
      sideAddresses,
      selectedBuilding.centroid,
    );
  }, [selectedBuilding, comments, sideAddresses]);

  // A district-only map selection (JAA-417): headed and labelled by district, no follow, no seeded listing.
  const noStreet = !!addressObject && !hasStreetAddress(addressObject);
  const districtName = selectedBuilding.properties?.name || addressObject?.city || '';

  const fullAddressLine = addressObject ? formatAddressLine(addressObject) : '';
  const addressString = noStreet ? districtName : fullAddressLine;
  const districtString = addressObject
    ? `${addressObject.postcode || ''}${
        addressObject.city ? ` ${addressObject.city}` : ''
      }`.trim()
    : '';

  /**
   * Primary frontage WITHOUT city (Figma 2625:38450): the city moves to the
   * district line; the headline is just "Street Number".
   */
  const primaryAddressLine = addressObject
    ? formatStreetLine(addressObject)
    : '';

  // The heading: the street line, or a district-only building's district.
  const headingText = noStreet ? districtName : primaryAddressLine;

  /**
   * Caption under the Street view / Map section titles (Figma 3058:76607):
   * "Street Number, Postcode City". Empty until the address resolves.
   */
  const sectionCaption = primaryAddressLine
    ? districtString
      ? `${primaryAddressLine}, ${districtString}`
      : primaryAddressLine
    : '';

  /**
   * Loading placeholder in the heading while the address is still resolving
   * (footprint probe / reverse-geocode) and nothing has resolved yet — the
   * heading would otherwise sit blank for ~0.5–1.3s.
   */
  const addressLoading = !addressObject && sideAddressesLoading;

  const primaryKey = addressKey(addressObject);
  /**
   * Corner-building detection: the OTHER street(s) this footprint fronts. The
   * hook returns every distinct frontage on the building (deduped by street,
   * capped at the geocoder's limit of 5 → primary + up to 4 sides); the ones
   * whose FULL address (street + number) differs from the primary become plain
   * "Street Number" lines (FrontageRun lists them as a comma-run). Excluding by
   * full address keeps a same-street second entrance from being collapsed away.
   * A side read from the map label (isLabelAddress) is skipped: it is this
   * building's single number, not another street it fronts, and would otherwise
   * show whenever a listing address differs from it (JAA-410).
   */
  const frontageLines = useMemo(
    () =>
      sideAddresses
        .filter((a) => a.street && !isLabelAddress(a) && addressKey(a) !== primaryKey)
        .map((a: Address) => formatStreetLine(a)),
    [sideAddresses, primaryKey],
  );

  // Which building the listings currently in state belong to; lets a refresh
  // for the SAME building keep showing them while the fetch runs.
  const lastListingsBuildingIdRef = useRef<string | null>(null);

  const trackLatestListings = useRef(onlyLatest<any[] | null>()).current;

  /**
   * Loads this building's own listings into `comments`. Only the latest call's
   * response is applied, so a slow response for a previous building (or an
   * older overlapping refresh) cannot overwrite the current listings or clear
   * the current loading state.
   */
  const fetchListings = () => {
    setIsLoadingComments(true);
    // The view is reused (not remounted) when switching buildings, so clear the
    // previous building's comments now — otherwise its listings/address linger
    // until this fetch resolves. On a SAME-building refresh (e.g. the
    // jaakko:listings-changed event) keep them: emptying collapses the card
    // rows for a frame and the page visibly flickers.
    if (lastListingsBuildingIdRef.current !== selectedBuilding.id) {
      setComments([]);
    }
    lastListingsBuildingIdRef.current = selectedBuilding.id;
    trackLatestListings(getBuildingListings(selectedBuilding.id), (rows) => {
      // null = dropped request (keep rows); [] = real empty answer.
      if (rows) setComments(rows as Comment[]);
      setIsLoadingComments(false);
    });
  };

  const fetchAreaDemand = () => {
    // Same reuse caveat as fetchListings: the view is reused when switching
    // buildings, so clear the previous building's area rows now. The helper
    // returns [] on failure, so a dropped request just leaves the row empty.
    // trackLatestAreaDemand drops out-of-order responses: without it a slow
    // response for the previous building would render under the current one.
    setAreaDemand([]);
    // No client point: the building's own centroid is the one answer every
    // surface can reach, and the map payload this view renders from often has
    // no centroid at all, so it used to ask from a street address instead - a
    // different question, with a different answer, from the one the listing
    // page asks about the same building (JAA-347).
    trackLatestAreaDemand(
      // The coordinate is a fallback the backend uses only for a building it
      // cannot place itself, so this view and the listing page still resolve the
      // same point for any building the database knows. A dropped request leaves
      // the row empty here, as it always has: the view clears the previous
      // building's rows on entry, so there is nothing to keep.
      getAreaInterestsForBuilding(selectedBuilding.id, {
        coords: getBuildingLikeCoordinates(selectedBuilding),
      }).then((rows) => rows ?? []),
      setAreaDemand,
    );
  };

  const fetchBuilding = () => {
    setIsLoadingBuilding(true);
    // Same reuse caveat as fetchListings: reset the previous building's stats so
    // the un-gated stats row doesn't show stale like/follower counts on switch.
    setLikeCount(0);
    setFollowerCount(0);
    setUserHasLiked(false);
    fetch(
      `${process.env.REACT_APP_API_URL}/building?id=${selectedBuilding.id}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data && data.properties) {
          setLikeCount(data.properties.like_count || 0);
          setFollowerCount(data.properties.subscribersCount || 0);
          setUserHasLiked(data.properties.user_has_liked || false);
        } else {
          setLikeCount(0);
          setFollowerCount(0);
          setUserHasLiked(false);
        }
        // Hand the hero the backend's complete footprint (the seed building
        // usually arrives without it). The hero trusts mapbox-union-v2 directly.
        if (data?.geometry && data?.geometry_source) {
          setFetchedGeometry({
            id: selectedBuilding.id,
            geometry: data.geometry,
            geometry_source: data.geometry_source,
          });
        }
      })
      .catch((error) => console.error('Error:', error))
      .finally(() => setIsLoadingBuilding(false));
  };

  // Outcome of an intent action (limit reached, already selling here). Was a
  // no-op here, which is why those paths fell back to window.alert.
  const [toastMessage, setToastMessage] = useState<OffMarketToastMessage | string | null>(null);
  const {
    isSubscribed: notificationsSubscriptionsToggle,
    setIsSubscribed: setNotificationsSubscriptionsToggle,
    isSaving: isSubmittingSubscription,
    addressPending: subscriptionAddressPending,
    disabled: subscriptionDisabled,
    toggle: handleToggleNotifications,
  } = useBuildingSubscription({
    building: selectedBuilding,
    address: addressObject,
    addressLoading: sideAddressesLoading,
    isLoggedIn,
    onLoginRequired: () => setLoginModalVisible(true),
    onError: () => setToastMessage(t('building.view.alertsError')),
    onChanged: (data) => {
      if (typeof data.subscribersCount === 'number') {
        setFollowerCount(Math.max(0, data.subscribersCount));
        onBuildingPreviewMetaChange?.(selectedBuilding.id, {
          followers: data.subscribersCount,
        });
      }
    },
  });

  const subscriptionStatusId = useId();
  // A district-only building's street never arrives, so do not say it is pending.
  const subscriptionPendingCopy = noStreet
    ? t('building.view.alertsNoStreet')
    : t('building.view.alertsAddressPending');
  const subscriptionStatus = subscriptionDisabled && subscriptionAddressPending ? (
    <p id={subscriptionStatusId} className="building-view__subscription-status" role="status">
      {subscriptionPendingCopy}
    </p>
  ) : null;

  const intent = useOffMarketIntent({
    selectedBuilding,
    resolvedAddress: addressObject,
    isLoggedIn,
    isLandingPage,
    setLoginModalVisible,
    refreshFeed,
    fetchListings,
    setNotificationsSubscriptionsToggle,
    setSlideToastIn: setToastMessage,
    fetchConversations,
    couldSellSelected,
    couldBuySelected,
    setCouldSellSelected,
    setCouldBuySelected,
  });

  const listings = useMemo(
    () =>
      comments.filter(
        (c) => c.feed_event_type === 'OFF_MARKET_WILLINGNESS',
      ) as unknown as OffMarketWillingnessEvent[],
    [comments],
  );
  const sellerListings = listings.filter((l) => l.could_sell);
  const buyerListings = listings.filter((l) => l.could_buy);

  // The viewer's own active listing here — drives the dark intent pill's manage
  // menu. Edit opens the PUT-update modal; Remove routes back through `intent`
  // (not a raw delete) so couldBuy/SellSelected stays in sync and the pill
  // disappears, rather than going stale until the next refetch.
  const ownListing = useMemo(
    () => listings.find((l) => l.isUserOwnEvent),
    [listings],
  );
  // Stub carries an empty `address` because the hook eagerly builds its
  // SharePromptModal (formatAddressString reads address.street) even while the
  // modals stay unrendered; a bare {} crashed on undefined address.
  const manageOwnListing = useListingManagement(
    (ownListing ?? { address: {} }) as unknown as ListingCardListing,
    { onChanged: fetchListings },
  );

  useEffect(() => {
    if (!isLandingPage && selectedBuilding) {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('building-id')) {
        url.searchParams.set('building-id', selectedBuilding.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    return () => {
      if (!isLandingPage) {
        const url = new URL(window.location.href);
        url.searchParams.delete('building-id');
        url.searchParams.delete('modal');
        url.searchParams.delete('payment');
        window.history.replaceState({}, '', url.toString());
      }
    };
  }, [isLandingPage, selectedBuilding]);

  useEffect(() => {
    fetchListings();
    fetchAreaDemand();
    fetchBuilding();
    const insightsRequestId = insightsRequestIdRef.current + 1;
    insightsRequestIdRef.current = insightsRequestId;
    const insightsBuildingId = selectedBuilding.id;
    setEmptyReport(null);
    setIsInsightsExpanded(false);
    fetchJaakkoComment({
      selectedBuilding,
      setEmptyReport: (report) => {
        if (insightsRequestIdRef.current === insightsRequestId) {
          setEmptyReport(report);
        }
      },
      setIsLoadingEmptyReport: (isLoading) => {
        if (insightsRequestIdRef.current !== insightsRequestId) return;
        setIsLoadingEmptyReport(isLoading);
        if (!isLoading) {
          setResolvedInsightsBuildingId(insightsBuildingId);
        }
      },
      language: i18n.language,
    });
    const nextCountry = addressObject?.country;
    if (nextCountry && nextCountry !== countryOfFeedContent) {
      setCountryOfFeedContent(nextCountry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding]);

  // The sell mutation now happens in the AMP flow, which lives outside this view
  // and only signals via the global `jaakko:listings-changed` event. Re-pull the
  // listings + the viewer's own sell/buy selection on that event so this view
  // flips to "You are selling here" and shows the new seller card after publish.
  // (Ref indirection keeps a single stable subscription despite the unmemoized
  // fetchers/intent recreated each render.)
  const refreshOnListingsChangedRef = useRef<() => void>(() => {});
  refreshOnListingsChangedRef.current = () => {
    fetchListings();
    fetchAreaDemand();
    intent.refetchIntent();
  };
  useEffect(() => {
    const handler = () => refreshOnListingsChangedRef.current();
    window.addEventListener('jaakko:listings-changed', handler);
    return () => window.removeEventListener('jaakko:listings-changed', handler);
  }, []);

  /**
   * Give the building its own history entry so a history pop (Back or
   * Forward) closes it. A pop off a listing, Profile or Messages opened over
   * the building is that layer closing, so the building stays (JAA-445).
   */
  useEffect(() => {
    const historyState = { modalOpen: true };
    window.history.pushState(historyState, '', window.location.href);
    let prevPathname = window.location.pathname;
    return history.listen((next: { pathname: string }, action: string) => {
      const closingLayerAbove =
        prevPathname.startsWith('/listing/') ||
        prevPathname.startsWith('/profile') ||
        prevPathname.startsWith('/direct-messages');
      prevPathname = next.pathname;
      if (action !== 'POP' || closingLayerAbove) return;
      toggleShowBuildingModal();
      setSelectedBuilding(null);
    });
  }, [history, toggleShowBuildingModal, setSelectedBuilding]);

  useEffect(() => {
    if (showBuildingModalTriggeredFromCouldBuyOrCouldSellModal) {
      setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal?.(null);
    }
  }, [
    showBuildingModalTriggeredFromCouldBuyOrCouldSellModal,
    setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal,
  ]);

  /**
   * Toggles the signed-in user's like on the open building. The heart and count
   * change only on a 2xx answer, so a refused or failed request (for example a
   * 400 for an id the backend rejects) leaves them as they were and shows a toast
   * instead of writing the error body into state.
   */
  const likeBuilding = () => {
    setIsSubmittingLike(true);
    const requestBuilding = withLikeRequestCoordinates(selectedBuilding);
    fetch(`${process.env.REACT_APP_API_URL}/like-building`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
      },
      body: JSON.stringify({
        like: !userHasLiked,
        selected_building: requestBuilding,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to toggle building like');
        return res.json();
      })
      .then((data: { like_count: number; user_has_liked: boolean }) => {
        setLikeCount(data.like_count);
        setUserHasLiked(data.user_has_liked);
        onBuildingPreviewMetaChange?.(selectedBuilding.id, {
          likes: data.like_count,
          liked: data.user_has_liked,
        });
        onBuildingLikeChange?.(requestBuilding, data.user_has_liked);
        window.dispatchEvent(new CustomEvent('jaakko:listings-changed'));
        refreshFeed();
      })
      .catch((error) => {
        console.error('Error:', error);
        setToastMessage(t('building.view.likeError'));
      })
      .finally(() => setIsSubmittingLike(false));
  };

  const handleShare = async () => {
    await shareUrlWithFallback({ url: buildingShareUrl(selectedBuilding.id), t });
  };

  const handleClose = () => {
    onReturnToMap?.(
      selectedBuilding.id,
      getBuildingLikeCoordinates(selectedBuilding) ?? undefined,
    );
    toggleShowBuildingModal();
    setSelectedBuilding(null);
  };

  /**
   * Rendered to match the Figma hero. Building-photo upload flow/endpoint is a
   * follow-up (no existing user-facing add-building-image API yet).
   */
  const handleAddImage = () => {
    if (!isLoggedIn) {
      setLoginModalVisible(true);
    }
  };

  /**
   * Add-image button — shared by the desktop hero banner (`banner` variant) and
   * the mobile full-bleed hero, so the markup lives in one place.
   */
  const renderAddImage = (variant?: 'banner') => (
    <button
      className={`building-view__icon-btn building-view__add-image${
        variant === 'banner' ? ' building-view__add-image--banner' : ''
      }`}
      onClick={handleAddImage}
      aria-label={t('building.view.addImage', 'Add image')}
    >
      <img
        className="building-view__icon"
        src={addImageIcon}
        alt=""
        aria-hidden="true"
      />
    </button>
  );

  /**
   * Fade hints on the swipeable bar title, driven by its own scrollLeft: right
   * fade only while there's more to the right, left fade once swiped past the
   * start — so no right fade at the end, and a left fade appears when scrolled.
   */
  const updateAddressFade = useCallback(() => {
    const addr = addressRef.current;
    if (!addr) return;
    const swipeable = addr.classList.contains(
      'building-view__address--morphing',
    );
    const max = addr.scrollWidth - addr.clientWidth;
    addr.classList.toggle(
      'building-view__address--fade-left',
      swipeable && addr.scrollLeft > 1,
    );
    addr.classList.toggle(
      'building-view__address--fade-right',
      swipeable && addr.scrollLeft < max - 1,
    );
  }, []);

  /**
   * Scrub the bar morph by how far the header card top sits below the screen top.
   * The morph runs across the bar's own height: p=0 the moment the header reaches
   * the bar's bottom edge, p=1 when it reaches the very top. The address is one
   * sticky element that scales + slides into the bar over this range — a single
   * continuous transform. --addr-cx is the px it must travel right to land
   * centred at the small size (measured, since the address width varies). Written
   * straight to the DOM (60fps); only the binary `showStickyHeader` flips state.
   */
  const updateTopbarProgress = useCallback(() => {
    const scrollEl = scrollRef.current;
    const sentinel = headerTopRef.current;
    const panel = panelRef.current;
    if (!scrollEl || !sentinel || !panel) return;
    const BAR_HEIGHT = 64;
    const dist =
      sentinel.getBoundingClientRect().top -
      scrollEl.getBoundingClientRect().top;
    const p = Math.min(1, Math.max(0, (BAR_HEIGHT - dist) / BAR_HEIGHT));
    panel.style.setProperty('--topbar-p', `${p}`);
    const addr = addressRef.current;
    if (addr) {
      addr.classList.toggle('building-view__address--morphing', p > 0);
      const GUTTER = 20;
      const SMALL = 18 / 32;
      const cx = (scrollEl.clientWidth - addr.offsetWidth * SMALL) / 2 - GUTTER;
      panel.style.setProperty('--addr-cx', `${Math.max(0, cx)}px`);
      updateAddressFade();
    }
    const intentEl = intentRef.current;
    if (intentEl) {
      const scrollTop = scrollEl.getBoundingClientRect().top;
      const intentBottom = intentEl.getBoundingClientRect().bottom;
      // Dock (push the bar up from the bottom) once the inline pills scroll up
      // to the topbar-bg bottom edge (64px). 48px hysteresis band on undock
      // keeps it from thrashing near the threshold.
      const TOPBAR_BG = 64;
      setCtaDocked((prev) =>
        prev
          ? intentBottom < scrollTop + TOPBAR_BG + 48
          : intentBottom < scrollTop + TOPBAR_BG,
      );
    }
    setShowStickyHeader(p >= 0.5);
  }, [updateAddressFade]);

  useEffect(() => {
    updateTopbarProgress();
  }, [isLoadingBuilding, updateTopbarProgress]);

  useEffect(() => {
    if (!showStickyHeader || isLoadingBuilding) setStickyMenuOpen(false);
  }, [showStickyHeader, isLoadingBuilding]);

  useEffect(() => {
    if (stickyMenuOpen) stickyMenuFirstItemRef.current?.focus();
  }, [stickyMenuOpen]);

  const insightsText: string = emptyReport?.analyysi || '';
  const insightsIsLong = insightsText.length > INSIGHTS_PREVIEW_CHARS;
  const isBuildingInfoLoading =
    isLoadingEmptyReport ||
    resolvedInsightsBuildingId !== selectedBuilding.id;
  const insightsDisplay =
    insightsIsLong && !isInsightsExpanded
      ? `${insightsText.slice(0, INSIGHTS_PREVIEW_CHARS).trimEnd()}…`
      : insightsText;

  // Combined buyer demand: building-level could-buys plus the area interests
  // covering this building (agreed: one merged count, consistent with the
  // explore pill's visibleCounts.buyers).
  const buyerCount = (intent.offMarketBuyerCount || 0) + areaDemand.length;

  /**
   * Seed for the AMP "Sell here" flow. The building + resolved address are
   * already known here, so the flow opens on the building step pre-filled and
   * the user only confirms (or changes the building / type) before continuing.
   * Prefers the backend footprint fetched for the hero when it matches.
   */
  const buildSellSeed = (): AddMyPlaceSeed | null => {
    if (!addressObject || noStreet) return null;
    const center =
      selectedBuilding.centroid ?? addressObject.coordinates ?? null;
    if (!center) return null;
    const street = [addressObject.street, addressObject.houseNumber]
      .filter(Boolean)
      .join(' ')
      .trim();
    const geometry = (fetchedGeometry?.id === selectedBuilding.id
      ? fetchedGeometry.geometry
      : selectedBuilding.geometry) as unknown as ListingBuildingGeometry | null;
    return {
      buildingId: selectedBuilding.id,
      address:
        street || primaryAddressLine || selectedBuilding.properties?.name || '',
      area: addressObject.city || '',
      postal: addressObject.postcode || '',
      center,
      addressObject: {
        country: addressObject.country || '',
        region: addressObject.region || '',
        city: addressObject.city || '',
        postcode: addressObject.postcode || '',
        street: addressObject.street || '',
        houseNumber: addressObject.houseNumber || '',
        coordinates: addressObject.coordinates ?? center,
      },
      geometry,
    };
  };

  /**
   * Sell CTAs open the AMP flow seeded with this building; a district-only
   * building opens it unseeded, at the address step, so the seller types the
   * real address. Falls back to the legacy details-modal intent flow if no
   * launcher was provided or the address hasn't resolved yet.
   */
  const handleSellHereClick = () => {
    const seed = buildSellSeed();
    if (onSellHere && (seed || noStreet)) {
      onSellHere(seed);
      return;
    }
    intent.handleCouldSellClick();
  };

  /**
   * Stats row adapts to data (Figma): buyers, likes, and followers only when >0.
   * Dots only between present items. ("{n} views" is in Figma too but has no
   * backend source yet.)
   */
  const renderStats = () => {
    const items: React.ReactNode[] = [];
    if (buyerCount > 0) {
      items.push(
        <span className="building-view__stat" key="buyers">
          <img
            className="building-view__stat-coin"
            src={coinIcon}
            alt=""
            aria-hidden="true"
          />
          {buyerCount}{' '}
          {buyerCount === 1
            ? t('building.view.buyer', 'buyer')
            : t('building.view.buyers', 'buyers')}
        </span>,
      );
    }
    if (likeCount > 0) {
      items.push(
        <span className="building-view__stat" key="likes">
          <img
            className="building-view__stat-heart"
            src={heartFilledIcon}
            alt=""
            aria-hidden="true"
          />
          {likeCount}
        </span>,
      );
    }
    if (followerCount > 0) {
      items.push(
        <span className="building-view__stat" key="followers">
          {followerCount}{' '}
          {followerCount === 1
            ? t('building.view.follower', 'follower')
            : t('building.view.followers', 'followers')}
        </span>,
      );
    }
    return items.flatMap((node, i) =>
      i === 0
        ? [node]
        : [<span className="building-view__stat-dot" key={`dot-${i}`} />, node],
    );
  };

  /**
   * Intent CTA is a state machine (Figma Building-view set):
   *  - selling active   -> dark full-width "You are selling here"
   *  - buying active    -> dark full-width "You have buying interest here"
   *  - sellers & buyers -> the same filled pills, without the prompt card
   *  - empty / partial  -> prompt CARD: graphic + copy + filled pill(s)
   *      · empty (0/0)        -> house+coin, both filled pills (yellow + teal)
   *      · only buyers (0 S)  -> house, full-width yellow "Sell here"
   *      · only sellers (0 B) -> coin,  full-width teal  "Buy here"
   * Helpers + early returns (no nested ternaries per CLAUDE.md).
   */
  const darkPill = (
    icon: string,
    labelKey: string,
    fallback: string,
    onRemove: () => void,
  ) => (
    <div className="building-view__intent-pill building-view__intent-pill--active-dark">
      <img
        className="building-view__intent-icon"
        src={icon}
        alt=""
        aria-hidden="true"
      />
      <span className="building-view__intent-label">
        <Trans i18nKey={labelKey}>{fallback}</Trans>
      </span>
      {/* The "..." opens Edit (PUT-update modal) / Share / Remove. Remove uses
          `onRemove` (the intent toggle) so the active flag clears and the pill
          hides; a raw delete would leave it stuck. Falls back to a static glyph
          in the rare window before the viewer's own listing is fetched. */}
      {ownListing ? (
        <ListingCardManageMenu
          onEdit={manageOwnListing.openEdit}
          onShare={manageOwnListing.openShare}
          onDelete={onRemove}
        />
      ) : (
        <img
          className="building-view__intent-overflow"
          src={overflowIcon}
          alt=""
          aria-hidden="true"
        />
      )}
    </div>
  );

  const filledPill = (
    variant: 'sell' | 'buy',
    labelKey: string,
    fallback: string,
    onClick: () => void,
    block = false,
  ) => (
    <button
      className={`building-view__intent-pill building-view__intent-pill--filled building-view__intent-pill--${variant}${
        block ? ' building-view__intent-pill--block' : ''
      }`}
      onClick={onClick}
    >
      <Trans i18nKey={labelKey}>{fallback}</Trans>
    </button>
  );

  const promptPills = (hasSellers: boolean, hasBuyers: boolean) => {
    return (
      <>
        {filledPill(
          'sell',
          'building.view.sellHere',
          'Sell here',
          handleSellHereClick,
        )}
        {filledPill(
          'buy',
          'building.view.buyHere',
          'Buy here',
          intent.handleCouldBuyClick,
        )}
      </>
    );
  };

  const renderPromptCard = (hasSellers: boolean, hasBuyers: boolean) => {
    let graphic: React.ReactNode;
    let copy: React.ReactNode;

    if (!hasSellers && !hasBuyers) {
      graphic = (
        <>
          <img
            className="building-view__prompt-coin building-view__prompt-coin--back"
            src={ctaCoinGraphic}
            alt=""
          />
          <img
            className="building-view__prompt-coin building-view__prompt-coin--front"
            src={ctaCoinGraphic}
            alt=""
          />
          <img
            className="building-view__prompt-house"
            src={ctaHouseGraphic}
            alt=""
          />
        </>
      );
      copy = (
        <Trans i18nKey="building.view.emptyPrompt">
          Be the first to sell or show you are interested in buying here.
        </Trans>
      );
    } else if (hasBuyers) {
      graphic = (
        <img
          className="building-view__prompt-house"
          src={ctaHouseGraphic}
          alt=""
        />
      );
      copy = (
        <Trans
          i18nKey="building.view.buyersHint"
          count={buyerCount}
          values={{ count: buyerCount }}
          components={{ b: <strong /> }}
        >
          {
            'There are <b>{{count}}</b> buyers for a home here. Be the first to sell.'
          }
        </Trans>
      );
    } else {
      const sellerCount = sellerListings.length;
      graphic = (
        <img
          className="building-view__prompt-coin"
          src={ctaCoinGraphic}
          alt=""
        />
      );
      copy = (
        <Trans
          i18nKey="building.view.sellersHint"
          count={sellerCount}
          values={{ count: sellerCount }}
          components={{ b: <strong /> }}
        >
          {
            'There are <b>{{count}}</b> sellers in this building. Be the first to buy.'
          }
        </Trans>
      );
    }

    return (
      <div className="building-view__prompt">
        <div className="building-view__prompt-top">
          <span className="building-view__prompt-graphic" aria-hidden="true">
            {graphic}
          </span>
          <p className="building-view__prompt-text">
            {copy}{' '}
            <a
              className="building-view__learn-more"
              href={
                i18n.language.startsWith('fi')
                  ? 'https://info.jaakko.com/nain-myyt-asunnon-itse'
                  : 'https://info.jaakko.com/en-gb/how-to-sell-your-apartment-yourself'
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <Trans i18nKey="building.view.learnMore">Learn more</Trans>
            </a>
          </p>
        </div>
        <div className="building-view__prompt-actions">
          {promptPills(hasSellers, hasBuyers)}
        </div>
      </div>
    );
  };

  /**
   * Intent variant: single resolver so the in-flow hint card and the sticky
   * action element stay in sync. Active and both-sides states are action-only;
   * empty and single-side states also surface an informational card.
   */
  const intentVariant = ():
    | 'sellingActive'
    | 'buyingActive'
    | 'both'
    | 'empty'
    | 'onlyBuyers'
    | 'onlySellers' => {
    if (couldSellSelected) return 'sellingActive';
    if (couldBuySelected) return 'buyingActive';
    const hasSellers = sellerListings.length > 0;
    // Area demand counts as buyer presence: the buyers-hint prompt exists to
    // motivate sellers with demand, and area interests are exactly that.
    const hasBuyers = buyerListings.length > 0 || areaDemand.length > 0;
    if (hasSellers && hasBuyers) return 'both';
    if (!hasSellers && !hasBuyers) return 'empty';
    return hasBuyers ? 'onlyBuyers' : 'onlySellers';
  };

  /** Action element for the sticky bottom CTA, present in every state. */
  const renderIntentActions = () => {
    const variant = intentVariant();
    if (variant === 'sellingActive') {
      return darkPill(
        homeIcon,
        'building.view.youAreSellingHere',
        'You are selling here',
        intent.handleCouldSellClick,
      );
    }
    if (variant === 'buyingActive') {
      return darkPill(
        coinIcon,
        'building.view.youHaveBuyingInterestHere',
        'You have buying interest here',
        intent.handleCouldBuyClick,
      );
    }
    // A building that already has listings gets the SAME filled pills as an
    // empty one: the ghost pair read as disabled next to the cards under them.
    return promptPills(variant === 'onlySellers', variant === 'onlyBuyers');
  };

  // Intent shown inline (in the prompt / after the header) and re-shown in the
  // docked CTA once it scrolls off the top.
  const renderIntent = () => {
    const variant = intentVariant();
    if (
      variant === 'empty' ||
      variant === 'onlyBuyers' ||
      variant === 'onlySellers'
    ) {
      return renderPromptCard(
        variant === 'onlySellers',
        variant === 'onlyBuyers',
      );
    }
    return renderIntentActions();
  };

  // The seller-card "Could buy" pill is a CTA to express buying interest, not a
  // per-listing toggle — so it must NOT reflect the building-level couldBuySelected
  // (that made every card look stuck-active once the viewer had buy interest).
  // Shares a listing via the backend share URL (OG preview + redirect to the
  // canonical single-listing route).
  const shareListingRoute = (l: OffMarketWillingnessEvent) => {
    shareUrlWithFallback({ url: listingShareUrl(l._id), t });
  };

  /**
   * Renders one seller listing as a big card. Its "Could buy" pill opens the
   * draft chat titled with this listing's own address, not the building's first
   * one, so a building with several entrances names the right apartment.
   */
  const renderCard = (listing: OffMarketWillingnessEvent) => (
    <ListingCard
      key={listing._id}
      type="seller"
      size="big"
      listing={listing as unknown as ListingCardListing}
      // Only so the "Could buy" pill knows not to raise its spinner over a
      // signed-out click, which opens the draft chat synchronously; login is
      // asked at Send (JAA-325).
      isLoggedIn={isLoggedIn}
      onOpen={() => openListing(listing)}
      onShare={() => shareListingRoute(listing)}
      // Dropped when the viewer already sells in this building: the backend
      // refuses one intent per building (USER_SELLER_AND_BUYER), so the pill
      // would 400 on click. Same reason the building-level Buyers CTA is
      // hidden — offering an action the server will reject is the bug this
      // page had before.
      onToggleCouldBuy={
        couldSellSelected
          ? undefined
          : () =>
              intent.handleCouldBuyForListing(
                String(listing._id),
                formatAddressLine(listing.address),
              )
      }
    />
  );

  const renderBuyerCard = (listing: OffMarketWillingnessEvent) => (
    <ListingCard
      key={listing._id}
      type="buyer"
      size="big"
      listing={listing as unknown as ListingCardListing}
      isLoggedIn={isLoggedIn}
      setLoginModalVisible={setLoginModalVisible}
      onOpen={() => openListing(listing)}
    />
  );

  // Area interests covering this building, trailing the building's own buyer
  // cards — the same DemandBuyerCard the explore list renders for
  // `area_demand` rows (areas line instead of an address).
  const renderAreaDemandCard = (card: AreaDemandCard) => (
    <DemandBuyerCard
      key={card.area_interest_id}
      card={card}
      isLoggedIn={isLoggedIn}
      setLoginModalVisible={setLoginModalVisible}
      onOpen={openListing}
    />
  );

  // Trailing CTA at the end of each carousel/row (Figma): prompts the opposite
  // intent. Shared so mobile carousels and the desktop card-rows render it once.
  // Dropped once the viewer holds either intent here: selling after buying is
  // refused by the backend (USER_SELLER_AND_BUYER), buying after selling by
  // handleCouldBuyClick (the area-interest flow has no backend check), so there
  // is nothing left to offer. React.Children.toArray discards the null, so no
  // empty slide or dot.
  const renderSellersCta = () => {
    if (couldSellSelected || couldBuySelected) return null;
    return (
      <div className="building-view__card-cta building-view__card-cta--sellers">
        <p className="building-view__card-cta-title">
          <Trans i18nKey="building.view.sellersCtaTitle">
            Couldn’t find what you needed?
          </Trans>
        </p>
        <p className="building-view__card-cta-body">
          <Trans i18nKey="building.view.sellersCtaBody">
            Let others know what you are looking for.
          </Trans>
        </p>
        <button
          className="building-view__card-cta-btn building-view__card-cta-btn--buy"
          onClick={intent.handleCouldBuyClick}
        >
          <span className="building-view__card-cta-plus">+</span>
          <Trans i18nKey="building.view.addBuyingInterest">
            Add buying interest
          </Trans>
        </button>
      </div>
    );
  };

  // Same rule mirrored: no "I could sell" prompt for a viewer who already sells
  // or buys in this building.
  const renderBuyersCta = () => {
    if (couldSellSelected || couldBuySelected) return null;
    return (
      <div className="building-view__card-cta building-view__card-cta--buyers">
        <p className="building-view__card-cta-body">
          <Trans i18nKey="building.view.buyersCtaBody">
            Do you own a home in this building? There is a good chance people
            are interested in buying it.
          </Trans>
        </p>
        <button
          className="building-view__card-cta-btn building-view__card-cta-btn--sell"
          onClick={handleSellHereClick}
        >
          <Trans i18nKey="building.view.iCouldSell">I could sell</Trans>
        </button>
      </div>
    );
  };

  /**
   * Building info + Street view + Map — identical on mobile sheet and desktop
   * column, so shared between both layouts.
   */
  const renderInfoSections = () => (
    <>
      <section className="building-view__section">
        <h3 className="building-view__section-title">
          <Trans i18nKey="building.view.buildingInfo">Building info</Trans>
        </h3>
        <div className="building-view__insights">
          {isBuildingInfoLoading ? (
            <BuildingInfoSkeleton />
          ) : insightsText ? (
            <p className="building-view__insights-text">
              {insightsDisplay}{' '}
              {insightsIsLong && (
                <button
                  className="building-view__read-more"
                  onClick={() => setIsInsightsExpanded((v) => !v)}
                >
                  {isInsightsExpanded
                    ? t('common.readLess', 'Read less')
                    : t('common.readMore', 'Read more')}
                </button>
              )}
            </p>
          ) : null}
        </div>
      </section>

      <section className="building-view__section">
        <h3 className="building-view__section-title">
          <Trans i18nKey="building.streetView.title">Street view</Trans>
        </h3>
        {sectionCaption && (
          <p className="building-view__section-caption">{sectionCaption}</p>
        )}
        <StreetView selectedBuilding={selectedBuilding} />
      </section>

      <section className="building-view__section">
        <h3 className="building-view__section-title">
          <Trans i18nKey="building.map.title">Map</Trans>
        </h3>
        {sectionCaption && (
          <p className="building-view__section-caption">{sectionCaption}</p>
        )}
        <BuildingLocationMap selectedBuilding={selectedBuilding} />
      </section>
    </>
  );

  if (isDesktop) {
    return (
      <div
        className="building-view building-view--desktop"
        role="dialog"
        aria-modal="true"
        aria-label={addressString || t('building.view.dialogLabel', 'Building')}
      >
        {/* Also rendered in the mobile branch below. Desktop is a separate
            return, so without this copy every intent outcome raised here was set
            into state that nothing displayed. */}
        <OffMarketToast message={toastMessage} onDismiss={() => setToastMessage(null)} />
        <PageMetaTags
          title={t('metaTags.building.title', { address: addressString })}
          description={t('metaTags.building.description', {
            address: addressString,
          })}
          ogTitle={t('metaTags.building.ogTitle')}
          ogDescription={t('metaTags.building.ogDescription', {
            address: addressString,
          })}
          ogImage={`${window.location.origin}/share/og-listing-house.png`}
          ogUrl={window.location.href}
        />

        <div className="building-view__page">
          <div className="building-view__content">
            <div className="building-view__page-actions">
              <button
                className="building-view__text-action"
                onClick={handleClose}
              >
                <img
                  className="building-view__text-action-icon"
                  src={viewMapIcon}
                  alt=""
                  aria-hidden="true"
                />
                <Trans i18nKey="building.view.viewMap">View map</Trans>
              </button>
              <div className="building-view__page-actions-right">
                <button
                  className="building-view__text-action"
                  onClick={handleShare}
                >
                  <img
                    className="building-view__text-action-icon"
                    src={shareIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  <Trans i18nKey="building.controls.share">Share</Trans>
                </button>
                <button
                  className="building-view__text-action"
                  disabled={isSubmittingLike}
                  onClick={() =>
                    isLoggedIn ? likeBuilding() : setLoginModalVisible(true)
                  }
                >
                  <img
                    className="building-view__text-action-icon"
                    src={userHasLiked ? heartFilledIcon : heartOutlineIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  <Trans i18nKey="building.controls.like">Like</Trans>
                </button>
              </div>
            </div>

            <div className="building-view__hero-banner">
              <BuildingHeroMap
                selectedBuilding={heroBuilding}
                onFootprintResolved={setHeroFootprint}
              />
              {SHOW_ADD_IMAGE && renderAddImage('banner')}
            </div>

            <div className="building-view__page-header">
              <h2 className="building-view__address">
                {addressLoading ? (
                  <span
                    className="building-view__address-skeleton"
                    aria-label={t('common.loading', 'Loading')}
                  />
                ) : (
                  headingText
                )}
              </h2>
              {noStreet && (
                <p className="building-view__no-street">
                  {t('building.view.noStreetAddress')}
                </p>
              )}
              <FrontageRun
                lines={frontageLines}
                district={districtString}
                loading={sideAddressesLoading}
              />
              <div className="building-view__info">
                <div className="building-view__stats">{renderStats()}</div>
                <button
                  className={`building-view__get-alerts${
                    notificationsSubscriptionsToggle
                      ? ' building-view__get-alerts--active'
                      : ''
                  }`}
                  onClick={handleToggleNotifications}
                  disabled={subscriptionDisabled}
                  aria-describedby={subscriptionStatus ? subscriptionStatusId : undefined}
                  title={
                    subscriptionAddressPending
                      ? subscriptionPendingCopy
                      : undefined
                  }
                  aria-busy={isSubmittingSubscription}
                  aria-pressed={notificationsSubscriptionsToggle}
                >
                  <img
                    className="building-view__get-alerts-icon"
                    src={bellIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  <Trans i18nKey="building.view.getAlerts">Get alerts</Trans>
                </button>
              </div>
              {subscriptionStatus}
              <div className="building-view__intent building-view__intent--desktop">
                {renderIntent()}
              </div>
            </div>

            {isLoadingBuilding ? (
              <div className="building-view__loading">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {sellerListings.length > 0 && (
                  <ListingCardRow
                    title={<Trans i18nKey="building.view.sellers">For sale in this building</Trans>}
                    ariaLabel={t('building.view.sellers', 'For sale in this building')}
                    headingLevel={3}
                    isDesktop
                    cards={sellerListings.map(renderCard)}
                    trailing={renderSellersCta()}
                    className="building-view__section"
                    titleClassName="building-view__section-title"
                    trackClassName="building-view__card-row"
                  />
                )}

                {(buyerListings.length > 0 || areaDemand.length > 0) && (
                  <ListingCardRow
                    title={<Trans i18nKey="building.view.buyersTitle">Interested buyers</Trans>}
                    ariaLabel={t('building.view.buyersTitle', 'Interested buyers')}
                    headingLevel={3}
                    isDesktop
                    cards={[
                      ...buyerListings.map(renderBuyerCard),
                      ...areaDemand.map(renderAreaDemandCard),
                    ]}
                    trailing={renderBuyersCta()}
                    className="building-view__section"
                    titleClassName="building-view__section-title"
                    trackClassName="building-view__card-row building-view__card-row--buyers"
                  />
                )}

                {renderInfoSections()}
              </>
            )}
          </div>
        </div>

        {showImageModal && (
          <ImageModal
            closeImageModal={() => setShowImageModal(null)}
            imageUrl={showImageModal}
          />
        )}

        <OffMarketIntentModals
          intent={intent}
          selectedBuilding={selectedBuilding}
          isLoggedIn={isLoggedIn}
          isLandingPage={isLandingPage}
          userEmail={userEmail}
          setLoginModalVisible={setLoginModalVisible}
        />
        {/* Edit modal for the dark intent pill's "..." menu (desktop return —
            the mobile return has its own copy). */}
        {ownListing && manageOwnListing.modals}
      </div>
    );
  }

  const currentIntentVariant = intentVariant();
  const shouldReserveDockedCtaSpace =
    !NON_DOCKABLE_VARIANTS.has(currentIntentVariant);

  return (
    <div
      className="building-view"
      role="dialog"
      aria-modal="true"
      aria-label={addressString || t('building.view.dialogLabel', 'Building')}
    >
      <OffMarketToast message={toastMessage} onDismiss={() => setToastMessage(null)} />
      <PageMetaTags
        title={t('metaTags.building.title', { address: addressString })}
        description={t('metaTags.building.description', {
          address: addressString,
        })}
        ogTitle={t('metaTags.building.ogTitle')}
        ogDescription={t('metaTags.building.ogDescription', {
          address: addressString,
        })}
        ogImage={`${window.location.origin}/share/og-listing-house.png`}
        ogUrl={window.location.href}
      />

      <div className="building-view__panel" ref={panelRef}>
        <div
          className="building-view__scroll"
          ref={scrollRef}
          onScroll={updateTopbarProgress}
        >
          <div className="building-view__visual">
            <BuildingHeroMap
              selectedBuilding={heroBuilding}
              onFootprintResolved={setHeroFootprint}
            />
            <div className="building-view__visual-gradient" />
            {SHOW_ADD_IMAGE && renderAddImage()}
          </div>

          {isLoadingBuilding ? (
            <div className="building-view__loading">
              <LoadingSpinner />
            </div>
          ) : (
            <div
              className={`building-view__sheet-body${
                shouldReserveDockedCtaSpace
                  ? ''
                  : ' building-view__sheet-body--no-cta-clearance'
              }`}
            >
              <div
                ref={headerTopRef}
                className="building-view__header-sentinel"
                aria-hidden="true"
              />
              <h2
                className="building-view__address"
                ref={addressRef}
                onScroll={updateAddressFade}
              >
                {addressLoading ? (
                  <span
                    className="building-view__address-skeleton"
                    aria-label={t('common.loading', 'Loading')}
                  />
                ) : (
                  headingText
                )}
              </h2>
              {noStreet && (
                <p className="building-view__no-street">
                  {t('building.view.noStreetAddress')}
                </p>
              )}
              <div className="building-view__header">
                <FrontageRun
                  lines={frontageLines}
                  district={districtString}
                  loading={sideAddressesLoading}
                />
                <div className="building-view__info">
                  <div className="building-view__stats">{renderStats()}</div>
                  <button
                    className={`building-view__bell${
                      notificationsSubscriptionsToggle
                        ? ' building-view__bell--active'
                        : ''
                    }`}
                    onClick={handleToggleNotifications}
                    disabled={subscriptionDisabled}
                    aria-describedby={subscriptionStatus ? subscriptionStatusId : undefined}
                    title={
                      subscriptionAddressPending
                        ? subscriptionPendingCopy
                        : undefined
                    }
                    aria-busy={isSubmittingSubscription}
                    aria-label={t('building.controls.follow')}
                    aria-pressed={notificationsSubscriptionsToggle}
                  >
                    <img
                      className="building-view__bell-icon"
                      src={bellIcon}
                      alt=""
                      aria-hidden="true"
                    />
                  </button>
                </div>
                {subscriptionStatus}
              </div>

              {isAdmin && (
                <div className="building-view__admin">
                  <AdminPrefilledListings
                    isAdmin={isAdmin}
                    selectedBuilding={selectedBuilding}
                  />
                </div>
              )}

              <div className="building-view__intent" ref={intentRef}>
                {renderIntent()}
              </div>

              {sellerListings.length > 0 && (
                <section className="building-view__section">
                  <h3 className="building-view__section-title">
                    <Trans i18nKey="building.view.sellers">For sale in this building</Trans>
                  </h3>
                  <HorizontalCarousel
                    ariaLabel={t('building.view.sellers', 'For sale in this building')}
                  >
                    {sellerListings.map(renderCard)}
                    {renderSellersCta()}
                  </HorizontalCarousel>
                </section>
              )}

              {(buyerListings.length > 0 || areaDemand.length > 0) && (
                <section className="building-view__section">
                  <h3 className="building-view__section-title">
                    <Trans i18nKey="building.view.buyersTitle">Interested buyers</Trans>
                  </h3>
                  <HorizontalCarousel
                    className="building-view__buyers-carousel"
                    ariaLabel={t('building.view.buyersTitle', 'Interested buyers')}
                  >
                    {buyerListings.map(renderBuyerCard)}
                    {areaDemand.map(renderAreaDemandCard)}
                    {renderBuyersCta()}
                  </HorizontalCarousel>
                </section>
              )}

              {renderInfoSections()}
            </div>
          )}
        </div>

        {/* Only actionable prompts float on scroll (see NON_DOCKABLE_VARIANTS). */}
        {shouldReserveDockedCtaSpace && (
          <div
            className={`building-view__cta${
              ctaDocked ? ' building-view__cta--docked' : ''
            }`}
            role="presentation"
          >
            {renderIntentActions()}
          </div>
        )}

        <div className="building-view__topbar-bg" aria-hidden="true" />

        <div
          className={`building-view__topbar${
            showStickyHeader && !isLoadingBuilding
              ? ' building-view__topbar--scrolled'
              : ''
          }`}
        >
          <button
            className="building-view__icon-btn building-view__topbar-back"
            onClick={handleClose}
            aria-label={t('common.back', 'Back')}
          >
            <img
              className="building-view__icon"
              src={backIcon}
              alt=""
              aria-hidden="true"
            />
          </button>
          <div className="building-view__topbar-actions">
            <div className="building-view__topbar-pair">
              <button
                className="building-view__icon-btn"
                onClick={handleShare}
                aria-label={t('building.controls.share')}
              >
                <img
                  className="building-view__icon"
                  src={shareIcon}
                  alt=""
                  aria-hidden="true"
                />
              </button>
              <button
                className={`building-view__icon-btn${userHasLiked ? ' building-view__icon-btn--active' : ''}`}
                onClick={() =>
                  isLoggedIn ? likeBuilding() : setLoginModalVisible(true)
                }
                disabled={isSubmittingLike}
                aria-label={t('building.controls.like')}
              >
                <img
                  className="building-view__icon"
                  src={userHasLiked ? heartFilledIcon : heartOutlineIcon}
                  alt=""
                  aria-hidden="true"
                />
              </button>
            </div>
            <div
              className="building-view__topbar-more"
              onKeyDown={(e) => {
                if (e.key === 'Escape' && stickyMenuOpen) {
                  setStickyMenuOpen(false);
                  stickyMoreRef.current?.focus();
                }
              }}
            >
              <button
                ref={stickyMoreRef}
                className="building-view__icon-btn"
                onClick={() => setStickyMenuOpen((v) => !v)}
                aria-label={t('building.controls.more', 'More')}
                aria-haspopup="menu"
                aria-expanded={stickyMenuOpen}
                tabIndex={showStickyHeader ? 0 : -1}
              >
                <img
                  className="building-view__icon building-view__sticky-more-icon"
                  src={overflowIcon}
                  alt=""
                  aria-hidden="true"
                />
              </button>
              {stickyMenuOpen && (
                <div
                  className="building-view__sticky-menu-backdrop"
                  onClick={() => setStickyMenuOpen(false)}
                  aria-hidden="true"
                />
              )}
              {stickyMenuOpen && (
                <div className="building-view__sticky-menu" role="menu">
                  <button
                    ref={stickyMenuFirstItemRef}
                    className="building-view__sticky-menu-item"
                    role="menuitem"
                    onClick={() => handleShare()}
                  >
                    <img
                      className="building-view__sticky-menu-icon"
                      src={shareIcon}
                      alt=""
                      aria-hidden="true"
                    />
                    {t('building.controls.share')}
                  </button>
                  <button
                    className="building-view__sticky-menu-item"
                    role="menuitem"
                    disabled={isSubmittingLike}
                    onClick={() => {
                      if (isLoggedIn) likeBuilding();
                      else setLoginModalVisible(true);
                    }}
                  >
                    <img
                      className="building-view__sticky-menu-icon"
                      src={userHasLiked ? heartFilledIcon : heartOutlineIcon}
                      alt=""
                      aria-hidden="true"
                    />
                    {t('building.controls.like')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showImageModal && (
        <ImageModal
          closeImageModal={() => setShowImageModal(null)}
          imageUrl={showImageModal}
        />
      )}

      <OffMarketIntentModals
        intent={intent}
        selectedBuilding={selectedBuilding}
        isLoggedIn={isLoggedIn}
        isLandingPage={isLandingPage}
        userEmail={userEmail}
        setLoginModalVisible={setLoginModalVisible}
      />
      {/* Edit modal for the dark intent pill's "..." menu. Its bundled
          delete/share dialogs stay dormant — Remove routes through intent. */}
      {ownListing && manageOwnListing.modals}
    </div>
  );
};

export default BuildingView;
